import { useState, useCallback, useRef } from 'react';

import { useUploadUrl } from './useLeadImportApi';
import { useImportWizardStore } from '../store/useImportWizardStore';
import { FILE_SIZE_THRESHOLD_BYTES } from '../utils/constants';

export interface UseFileUploadReturn {
  upload: (file: File) => Promise<void>;
  progress: number; // 0-100
  isUploading: boolean;
  error: string | null;
  estimatedTime: number | null; // seconds, shown for files > 10MB
}

/**
 * Hook that handles file upload to S3 via presigned URL with progress tracking.
 *
 * - Requests a presigned URL from the Lead Import Service
 * - Uploads the file directly to S3 using XHR PUT
 * - Tracks upload progress via xhr.upload progress events
 * - Handles presigned URL expiry (403 from S3)
 * - Calculates estimated upload time for files > 10MB
 * - Updates the wizard store with fileID and filename on success
 */
export function useFileUpload(): UseFileUploadReturn {
  const [progress, setProgress] = useState<number>(0);
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [estimatedTime, setEstimatedTime] = useState<number | null>(null);

  const uploadStartTimeRef = useRef<number>(0);

  const uploadUrlMutation = useUploadUrl();
  const setFileData = useImportWizardStore((state) => state.setFileData);

  const upload = useCallback(
    async (file: File) => {
      setProgress(0);
      setIsUploading(true);
      setError(null);
      setEstimatedTime(null);

      try {
        // Step 1: Get presigned URL from the Lead Import Service
        const { uploadUrl, fileID } = await uploadUrlMutation.mutateAsync({
          filename: file.name,
          contentType: file.type,
        });

        // Step 2: Upload file to S3 via XHR PUT with progress tracking
        await uploadToS3(uploadUrl, file, (pct, estTime) => {
          setProgress(pct);
          if (file.size > FILE_SIZE_THRESHOLD_BYTES) {
            setEstimatedTime(estTime);
          }
        });

        // Step 3: Update wizard store with file data on success
        setFileData({ fileID, filename: file.name });
      } catch (err: unknown) {
        const message =
          err instanceof Error ? err.message : 'Upload failed unexpectedly';
        setError(message);
      } finally {
        setIsUploading(false);
      }
    },
    [uploadUrlMutation, setFileData]
  );

  /**
   * Uploads a file to S3 using XHR PUT with progress tracking.
   * Handles 403 as presigned URL expiry.
   */
  function uploadToS3(
    url: string,
    file: File,
    onProgress: (pct: number, estimatedTimeRemaining: number | null) => void
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      uploadStartTimeRef.current = Date.now();

      xhr.upload.addEventListener('progress', (e) => {
        if (e.lengthComputable) {
          const pct = Math.round((e.loaded / e.total) * 100);
          let estTime: number | null = null;

          if (file.size > FILE_SIZE_THRESHOLD_BYTES && pct > 0) {
            const elapsedMs = Date.now() - uploadStartTimeRef.current;
            const elapsedSec = elapsedMs / 1000;
            const rate = pct / elapsedSec; // percent per second
            const remainingPct = 100 - pct;
            estTime = rate > 0 ? Math.ceil(remainingPct / rate) : null;
          }

          onProgress(pct, estTime);
        }
      });

      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else if (xhr.status === 403) {
          reject(new Error('Upload expired, please try again'));
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });

      xhr.addEventListener('error', () => {
        reject(new Error('Network error during upload'));
      });

      xhr.open('PUT', url);
      xhr.setRequestHeader('Content-Type', file.type);
      xhr.send(file);
    });
  }

  return {
    upload,
    progress,
    isUploading,
    error,
    estimatedTime,
  };
}
